import { useState } from 'react';

import { ExternalLinkIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { DialogTooltipProps } from '@/shared/types/dialogTooltip';

import { CollapseButton } from './CollapseButton';
import { ConvertButton } from './ConvertButton';
import { TooltipButton } from './TooltipButton';

import styles from './DialogTooltip.module.scss';

const DialogTooltip = ({
  children,
  onDelete = null,
  onNavigate = null,
  onTriggerConvert = null,
  onCollapse = null,
  isCollapsed = null,
  show = true,
  ...props
}: DialogTooltipProps) => {
  const { type } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const title = (
    <div className={styles.dialogTooltip} data-testid="dialog-tooltip">
      {type === 'condition' && onCollapse && (
        <CollapseButton isCollapsed={isCollapsed} onCollapse={onCollapse} />
      )}
      {onDelete && (
        <TooltipButton title={t('common.delete')} onClick={onDelete}>
          <Trash2Icon size={16} className="text-background" />
        </TooltipButton>
      )}
      {(type === 'intent' || type === 'event') && (
        <>
          {onNavigate && (
            <TooltipButton title={t('common.navigate')} onClick={onNavigate}>
              <ExternalLinkIcon size={16} className="text-background" />
            </TooltipButton>
          )}
          {onTriggerConvert && <ConvertButton type={type} onTriggerConvert={onTriggerConvert} />}
        </>
      )}
    </div>
  );

  if (!show) {
    return children;
  }

  // A Popover rather than a tooltip: the toolbar's own buttons carry tooltips, and Radix
  // closes every open tooltip whenever another opens, so a nested tooltip would dismiss
  // this toolbar the moment a button label appeared.
  return (
    <Popover open={open}>
      {/*
        An anchor rather than a trigger: `PopoverTrigger` would stamp `type="button"` and
        `aria-haspopup="dialog"` onto the children, which are action boxes and option rows.
      */}
      <PopoverAnchor asChild onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        {children}
      </PopoverAnchor>
      <PopoverContent
        side="top"
        // Matches the 14px gap MUI's tooltip used for top placement.
        sideOffset={14}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        // `open` is controlled with no `onOpenChange`, so Radix's own dismiss is a no-op.
        // Close on Escape like MUI's tooltip did; a click still leaves the toolbar up.
        onEscapeKeyDown={() => setOpen(false)}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="w-auto border-none bg-transparent p-0 shadow-none"
      >
        {title}
      </PopoverContent>
    </Popover>
  );
};

export default DialogTooltip;
